import React, { useEffect, useState } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Login from "./pages/Login/Login";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import { RootState } from "./store";
import { logout, restoreSession } from "./store/slices/authSlice";
import UserProfile from "./pages/UserProfile/UserProfile";
import DashboardOverviewSection from "./pages/DashboardOverview/DashboardOverviewSection";
import StationOnboardingSection from "./pages/StationOnboardingSection/StationOnboardingSection";
import StationDashboardPage from "./pages/StationOnboardingSection/StationDashboardPage";
import UsersAndRolesSection from "./pages/UsersAndRolesSection/UsersAndRolesSection";
import AuditLogsSection from "./pages/AuditLogsSection/AuditLogsSection";
import GenerateQrCodes from "./pages/GenerateQrCodes/GenerateQrCodes";
import ChangePasswordPage from "./pages/ChangePasswordPage/ChangePasswordPage";
import {
  getStoredAccessToken,
  getStoredUser,
  isAccessTokenExpired,
  isRefreshTokenExpired,
} from "./utils/authStorage";
import { refreshAccessToken } from "./api/authSession";
import RegisteredVehicles from "./pages/RegisteredVehicles/RegisteredVehicles";
import VehicleDetails from "./pages/RegisteredVehicles/VehicleDetails";

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      const storedUser = getStoredUser();
      if (!storedUser) {
        if (isMounted) {
          setIsAuthInitializing(false);
        }
        return;
      }

      if (isRefreshTokenExpired()) {
        dispatch(logout());
        if (isMounted) {
          setIsAuthInitializing(false);
        }
        return;
      }

      let accessToken = getStoredAccessToken();
      if (!accessToken || isAccessTokenExpired(accessToken)) {
        accessToken = await refreshAccessToken();
      }

      if (accessToken) {
        dispatch(
          restoreSession({
            user: storedUser,
            token: accessToken,
          }),
        );
      } else {
        dispatch(logout());
      }

      if (isMounted) {
        setIsAuthInitializing(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  if (isAuthInitializing) {
    return null;
  }

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/my-profile"
          element={<Navigate to="/dashboard/my-profile" replace />}
        />
        <Route
          path="/change-password"
          element={<Navigate to="/dashboard/change-password" replace />}
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<DashboardOverviewSection />} />
          <Route
            path="station-onboarding"
            element={<StationOnboardingSection />}
          />
          <Route
            path="station-onboarding/:stationId"
            element={<StationDashboardPage />}
          />
          <Route path="users-and-roles" element={<UsersAndRolesSection />} />
          <Route path="audit-logs" element={<AuditLogsSection />} />
          <Route path="generate-qr-codes" element={<GenerateQrCodes />} />
          <Route path="registered-vehicles" element={<RegisteredVehicles />} />
          <Route
            path="registered-vehicles/:vehicleId"
            element={<VehicleDetails />}
          />
          <Route path="my-profile" element={<UserProfile />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
        </Route>
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
